import { Grid } from "@mui/material";
import { useEffect, useState } from 'react';
import { CarouselItem } from './CarouselItem';
import { Indicators } from './Indicators';
import {
    StyledButtonWrapper,
    StyledIconButton,
    StyledItemWrapper,
    StyledRoot
} from './Styled';
import { CarouselProps } from './types';
import { sanitizeProps, useInterval } from './util';


export const Carousel = (props: CarouselProps) => {

    const [state, setState] = useState({
        active: 0,
        prevActive: 0,
        next: true
    });

    /** Used to set carousel's height. It is being set by the CarouselItems */
    const [childrenHeight, setChildrenHeight] = useState<number>();
    const [paused, setPaused] = useState<boolean>(false);

    const sanitizedProps = sanitizeProps(props);

    // componentDidMount & onIndexChange
    useEffect(() => {
        const { index, changeOnFirstRender } = sanitizedProps;
        setNext(index, true, changeOnFirstRender);
    }, [sanitizedProps.index]);


    useInterval(() => {
        const { autoPlay } = sanitizedProps;

        if (autoPlay && !paused) {
            next(undefined);
        }

    }, sanitizedProps.interval);

    const slideCount = Array.isArray(props.children) ? props.children.length : 0;

    const next = (event: any) => {
        const { children, cycleNavigation } = sanitizedProps;

        let last = Array.isArray(children) ? children.length - 1 : 0;
        const nextActive = state.active + 1 > last ? (cycleNavigation ? 0 : state.active) : state.active + 1;

        setNext(nextActive, true);

        if (event)
            event.stopPropagation();
    };

    const prev = (event: any) => {
        const { children, cycleNavigation } = sanitizedProps;

        let last = Array.isArray(children) ? children.length - 1 : 0;
        const nextActive = state.active - 1 < 0 ? (cycleNavigation ? last : state.active) : state.active - 1;

        setNext(nextActive, false);

        if (event)
            event.stopPropagation();
    };

    const setNext = (index: number, isNext: boolean, runCallbacks: boolean = true) => {
        const { onChange, children, strictIndexing } = sanitizedProps;

        if (Array.isArray(children)) {
            if (strictIndexing && index > children.length - 1) index = children.length - 1;
            if (strictIndexing && index < 0) index = 0;
        }
        else {
            index = 0;
        }

        if (runCallbacks) {
            if (isNext !== undefined)
                isNext ? sanitizedProps.next(index, state.active) : sanitizedProps.prev(index, state.active);

            onChange(index, state.active);
        }

        if (isNext === undefined) {
            isNext = index > state.active;
        }

        setState({
            active: index,
            prevActive: state.active,
            next: isNext
        });
    };

    const {
        children,
        sx,
        className,

        height,

        stopAutoPlayOnHover,
        animation,
        duration,
        swipe,

        navButtonsAlwaysInvisible,
        navButtonsAlwaysVisible,
        cycleNavigation,
        fullHeightHover,
        navButtonsProps,
        navButtonsWrapperProps,
        NavButton,

        NextIcon,
        PrevIcon,

        indicators,
        indicatorContainerProps,
        indicatorIconButtonProps,
        activeIndicatorIconButtonProps,
        IndicatorIcon,

        FooterLeftColumn,
        FooterRightColumn,

        offsetTop,
        observeWindowHeight
    } = sanitizedProps;

    const showButton = (next = true) => {
        if (cycleNavigation) return true;

        const last = Array.isArray(children) ? children.length - 1 : 0;

        if (next && state.active === last) return false;
        if (!next && state.active === 0) return false;

        return true;
    };

    const itemHeight = observeWindowHeight
        ? Math.min(height ?? childrenHeight ?? 0, childrenHeight ?? 0) - offsetTop
        : height ?? childrenHeight ?? 0;

    return (
        <StyledRoot
            sx={sx}
            className={className}
            onMouseOver={() => { stopAutoPlayOnHover && setPaused(true); }}
            onMouseOut={() => { stopAutoPlayOnHover && setPaused(false); }}
            onFocus={() => { stopAutoPlayOnHover && setPaused(true); }}
            onBlur={() => { stopAutoPlayOnHover && setPaused(false); }}
        >
            <StyledItemWrapper
                id="carousel-item-wrapper"
                style={{
                    minHeight: observeWindowHeight ? 0 : undefined,
                    maxHeight: observeWindowHeight ? itemHeight : undefined
                }}
            >
                {
                    Array.isArray(children) ?
                        children.map((child, index) => {
                            return (
                                <CarouselItem
                                    key={`carousel-item${index}`}
                                    state={state}
                                    index={index}
                                    maxIndex={children.length - 1}
                                    child={child}
                                    animation={animation}
                                    duration={duration / 100}
                                    swipe={swipe}
                                    next={next}
                                    prev={prev}
                                    setChildHeight={setChildrenHeight}
                                    observeWindowHeight={observeWindowHeight!}
                                />
                            );
                        })
                        :
                        <CarouselItem
                            key={`carousel-item0`}
                            state={state}
                            index={0}
                            maxIndex={0}
                            child={children}
                            animation={animation}
                            duration={duration / 1000}
                            setChildHeight={setChildrenHeight}
                            observeWindowHeight={observeWindowHeight!}
                        />
                }
            </StyledItemWrapper>


            {!navButtonsAlwaysInvisible && showButton(true) && slideCount > 1 &&
                <StyledButtonWrapper $next $prev={false} $fullHeightHover={fullHeightHover} {...navButtonsWrapperProps}>
                    {NavButton !== undefined ?
                        NavButton({ onClick: next, next: true, prev: false, ...navButtonsProps })
                        :
                        <StyledIconButton
                            $alwaysVisible={navButtonsAlwaysVisible}
                            $fullHeightHover={fullHeightHover}
                            onClick={next}
                            aria-label="Next"
                            {...navButtonsProps}
                        >
                            {NextIcon}
                        </StyledIconButton>
                    }
                </StyledButtonWrapper>
            }

            {!navButtonsAlwaysInvisible && showButton(false) && slideCount > 1 &&
                <StyledButtonWrapper $next={false} $prev $fullHeightHover={fullHeightHover} {...navButtonsWrapperProps}>
                    {NavButton !== undefined ?
                        NavButton({ onClick: prev, next: false, prev: true, ...navButtonsProps })
                        :
                        <StyledIconButton
                            $alwaysVisible={navButtonsAlwaysVisible}
                            $fullHeightHover={fullHeightHover}
                            onClick={prev}
                            aria-label="Previous"
                            {...navButtonsProps}
                        >
                            {PrevIcon}
                        </StyledIconButton>
                    }
                </StyledButtonWrapper>
            }

            <Grid container justifyContent="center" alignItems="center">
                {FooterLeftColumn &&
                    <Grid>{FooterLeftColumn}</Grid>
                }

                {
                    indicators
                        ? <Grid size="grow">
                            <Indicators
                                length={Array.isArray(children) ? children.length : 0}
                                active={state.active}
                                press={setNext}
                                indicatorContainerProps={indicatorContainerProps}
                                indicatorIconButtonProps={indicatorIconButtonProps}
                                activeIndicatorIconButtonProps={activeIndicatorIconButtonProps}
                                IndicatorIcon={IndicatorIcon}
                            />
                        </Grid>
                        : null
                }

                {FooterRightColumn &&
                    <Grid>{FooterRightColumn}</Grid>
                }
            </Grid>
        </StyledRoot>
    );
};

export default Carousel;
